import Button from '@/components/ui/button';
import { Image } from '@/components/ui/image';
import { siteSettings } from '@/config/site';
import { Routes } from '@/config/routes';
import { useBuyTogetherProduct } from '@/framework/product';
import { gtmAddToCart, productToGTMItem } from '@/lib/gtm';
import usePrice from '@/lib/use-price';
import { useCart } from '@/store/quick-cart/cart.context';
import { generateCartItem } from '@/store/quick-cart/generate-cart-item';
import type { IBuyTogetherProduct, ISearchProduct } from '@/types';
import classNames from 'classnames';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

type BuyTogetherProps = {
  mainProduct: ISearchProduct;
  mainSalePrice?: number;
  className?: string;
  variant?: 'desktop' | 'mobile';
  isModal?: boolean;
  onAssociatedProductNavigate?: (path: string) => void;
};

function OperatorBadge({
  children,
  className,
  size = 'md',
}: Readonly<{
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}>) {
  return (
    <span
      className={classNames(
        'flex shrink-0 items-center justify-center rounded-full border border-border-200 bg-white font-bold leading-none text-body shadow-[0_2px_6px_rgba(0,0,0,0.08)]',
        size === 'sm' ? 'h-6 w-6 text-sm' : 'h-8 w-8 text-base md:h-9 md:w-9 md:text-lg',
        className,
      )}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

function AssociatedProductLink({
  product,
  children,
  className,
  onNavigate,
}: Readonly<{
  product: Pick<IBuyTogetherProduct, 'slug' | 'sku' | 'name'>;
  children: React.ReactNode;
  className?: string;
  onNavigate?: (path: string) => void;
}>) {
  const href = Routes.product(product.slug, product.sku);

  return (
    <Link
      href={href}
      onClick={(event) => {
        if (!onNavigate) return;
        event.preventDefault();
        onNavigate(href);
      }}
      className={classNames(
        'transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
      aria-label={`Ver produto ${product.name}`}
    >
      {children}
    </Link>
  );
}

function ProductThumb({
  product,
  image,
  size = 'md',
}: Readonly<{
  product: Pick<ISearchProduct, 'name'>;
  image?: ISearchProduct['image'] | IBuyTogetherProduct['image'];
  size?: 'sm' | 'md';
}>) {
  const src = image?.thumbnail || image?.original;
  const sizeClass =
    size === 'sm'
      ? 'h-14 w-14'
      : 'h-16 w-16 md:h-[72px] md:w-[72px]';

  return (
    <div
      className={classNames(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-200 bg-white',
        sizeClass,
      )}
    >
      {src ? (
        <Image
          src={src}
          alt={product.name}
          width={72}
          height={72}
          className="h-full w-full object-contain"
        />
      ) : (
        <span className="px-1 text-center text-[10px] text-muted">Sem imagem</span>
      )}
    </div>
  );
}

export default function BuyTogether({
  mainProduct,
  mainSalePrice,
  className,
  variant = 'desktop',
  isModal = false,
  onAssociatedProductNavigate,
}: Readonly<BuyTogetherProps>) {
  const { addItemToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const { data: buyTogetherProduct, isLoading } = useBuyTogetherProduct(
    mainProduct.product_id,
  );

  const mainPrice =
    mainSalePrice ?? mainProduct.sale_price ?? mainProduct.price ?? 0;

  const totalAmount = useMemo(() => {
    if (!buyTogetherProduct) return 0;
    return (
      mainPrice +
      (buyTogetherProduct.sale_price ?? buyTogetherProduct.price ?? 0)
    );
  }, [buyTogetherProduct, mainPrice]);

  const { price: associatedPriceFormatted } = usePrice({
    amount: buyTogetherProduct?.sale_price ?? buyTogetherProduct?.price ?? 0,
  });
  const { price: totalPriceFormatted } = usePrice({ amount: totalAmount });

  if (isLoading || !buyTogetherProduct || Number(buyTogetherProduct.quantity) <= 0) {
    return null;
  }

  const sendGTMBuyTogetherAddToCart = (
    mainItem: ReturnType<typeof generateCartItem>,
    associatedItem: ReturnType<typeof generateCartItem>,
    bundleValue: number,
  ) => {
    try {
      const currency = siteSettings.currencyCode || 'BRL';

      gtmAddToCart({
        add_to_cart_source: 'buy_together',
        main_product_id: String(mainProduct.product_id),
        ecommerce: {
          currency,
          value: bundleValue,
          items: [
            productToGTMItem(mainItem, 1, [], mainProduct.brand ?? ''),
            productToGTMItem(
              associatedItem,
              1,
              [],
              buyTogetherProduct.manufacturer ?? '',
            ),
          ],
        },
      });
    } catch (error) {
      console.warn('[GTM] Failed to send add_to_cart event:', error);
    }
  };

  const handleBuyTogether = () => {
    if (isAdding) return;

    const mainStock = Number(mainProduct.quantity) || 0;
    const associatedStock = Number(buyTogetherProduct.quantity) || 0;

    if (mainStock <= 0 || associatedStock <= 0) {
      toast.error('Um dos produtos está sem estoque.');
      return;
    }

    setIsAdding(true);

    try {
      const shopSource = buyTogetherProduct.shop ?? mainProduct.shop;
      const associatedPrice =
        buyTogetherProduct.sale_price ?? buyTogetherProduct.price ?? 0;

      const mainItem = generateCartItem({
        ...mainProduct,
        sale_price: mainPrice,
        price: mainProduct.price ?? mainPrice,
      });

      const associatedItem = generateCartItem({
        ...buyTogetherProduct,
        brand: buyTogetherProduct.manufacturer ?? '',
        category: '',
        manufacturer: buyTogetherProduct.manufacturer ?? '',
        unit: buyTogetherProduct.unit ?? mainProduct.unit,
        shop: shopSource ? { id: String(shopSource.id) } : undefined,
      } as ISearchProduct);

      addItemToCart(mainItem, 1);
      addItemToCart(associatedItem, 1);
      sendGTMBuyTogetherAddToCart(
        mainItem,
        associatedItem,
        mainPrice + associatedPrice,
      );
      toast.success('Produtos adicionados ao carrinho!');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <section
      className={classNames(
        'rounded-2xl border border-border-200 bg-white shadow-sm',
        isModal ? 'p-3' : 'p-4',
        variant === 'desktop' &&
          (isModal
            ? 'relative z-10 w-full max-w-full'
            : 'relative z-10 w-max min-w-full max-w-none'),
        className,
      )}
      aria-label="Comprar junto"
    >
      {variant === 'desktop' ? (
        <div
          className={classNames(
            'flex items-center',
            isModal
              ? 'flex-wrap gap-2'
              : 'flex-nowrap gap-2 md:gap-2.5 lg:gap-3',
          )}
        >
          <ProductThumb
            product={mainProduct}
            image={mainProduct.image}
            size={isModal ? 'sm' : 'md'}
          />

          <OperatorBadge size={isModal ? 'sm' : 'md'}>+</OperatorBadge>

          <AssociatedProductLink
            product={buyTogetherProduct}
            className="flex min-w-0 shrink items-center gap-2"
            onNavigate={onAssociatedProductNavigate}
          >
            <ProductThumb
              product={buyTogetherProduct}
              image={buyTogetherProduct.image}
              size={isModal ? 'sm' : 'md'}
            />

            <div
              className={classNames(
                'min-w-0 shrink',
                isModal ? 'w-[min(110px,22vw)]' : 'w-[min(180px,28vw)] shrink-0',
              )}
            >
              <p className="line-clamp-2 text-xs font-medium leading-snug text-body md:text-sm">
                {buyTogetherProduct.name}
              </p>
              <p className="mt-0.5 text-xs font-semibold text-dark-blue md:text-sm">
                {associatedPriceFormatted}
              </p>
            </div>
          </AssociatedProductLink>

          <OperatorBadge size={isModal ? 'sm' : 'md'}>=</OperatorBadge>

          <div className="shrink-0 whitespace-nowrap">
            <p className="text-xs text-body md:text-sm">Por:</p>
            <p
              className={classNames(
                'font-semibold text-dark-blue',
                isModal ? 'text-base' : 'text-lg md:text-xl',
              )}
            >
              {totalPriceFormatted}
            </p>
          </div>

          <Button
            onClick={handleBuyTogether}
            loading={isAdding}
            disabled={isAdding}
            size="small"
            className={classNames(
              'shrink-0 whitespace-nowrap',
              isModal ? '!h-9 !px-3 text-xs' : '!h-11 !px-5',
            )}
          >
            Comprar junto
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2.5">
          <div className="flex min-w-0 flex-1 gap-2">
            <div className="flex shrink-0 flex-col justify-between gap-2 py-0.5">
              <ProductThumb
                product={mainProduct}
                image={mainProduct.image}
                size="sm"
              />
              <AssociatedProductLink
                product={buyTogetherProduct}
                onNavigate={onAssociatedProductNavigate}
              >
                <ProductThumb
                  product={buyTogetherProduct}
                  image={buyTogetherProduct.image}
                  size="sm"
                />
              </AssociatedProductLink>
            </div>

            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
              <p className="line-clamp-2 text-xs font-medium leading-snug text-body">
                {mainProduct.name}
              </p>

              <div className="flex pl-1">
                <OperatorBadge size="sm">+</OperatorBadge>
              </div>

              <AssociatedProductLink
                product={buyTogetherProduct}
                onNavigate={onAssociatedProductNavigate}
              >
                <p className="line-clamp-2 text-xs font-medium leading-snug text-body">
                  {buyTogetherProduct.name}
                </p>
              </AssociatedProductLink>
            </div>
          </div>

          <OperatorBadge size="sm" className="self-center">
            =
          </OperatorBadge>

          <div className="flex w-[40%] max-w-[152px] shrink-0 flex-col items-center gap-2">
            <p className="text-base font-semibold text-body">Compre junto</p>
            <div className="flex items-baseline gap-1 whitespace-nowrap text-right">
              <span className="text-xs text-body">Por:</span>
              <span className="text-lg font-semibold leading-none text-dark-blue">
                {totalPriceFormatted}
              </span>
            </div>
            <Button
              onClick={handleBuyTogether}
              loading={isAdding}
              disabled={isAdding}
              size="small"
              className="w-full !px-4 text-xs"
            >
              Comprar junto
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
